import useToast from '@/hooks/useToast';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useContext, useState } from 'react';
import { useQueryClient } from 'react-query';
import { postJoinGroup } from '../../../../apis/tms/feed.api';
import { leaveFolderOrGroups } from '../../../../apis/tms/share';
import { checkIsUserJoined } from '../../../../common/checkRoleUser';
import { PATH_NAME } from '../../../../configs/pathName';
import {
  FEED_GROUPS_SUGGESTION,
  GROUP_ROLE_ACCESS,
  MEMBER_LIST,
  USER_JOINED_GROUPS,
  USER_JOINED_GROUPS_USER_WALL,
} from '../../../../constant/QueryKeyName';
import { ROLE_ACCESS, TRoleAccess } from '../../../../constant/storage';
import { copyToClipboard } from '../../../../helpers/copyToClipboard';
import { FolderStatusEnum } from '../../../../interfaces/training.interface';
import AuthContext from '../../../../state/auth/AuthContext';
import LeaveGroupBtn from '../../../buttons/training-module/LeaveGroupButton';
import RequestJoinGroupButton from '../../../buttons/training-module/RequestJoinGroupButton';
import { QuickLoginModalHOC } from '../../../modal/LoginModal2HOC';
import ShareButton from './ShareButton';

const Accessibility = dynamic(
  () => import('../../../modal/Accessibility.modal')
);

function OtherRoleButtonGroups({
  mineRole,
  folderId,
  folderStatus,
  folder,
}: {
  mineRole: TRoleAccess;
  folderId: string;
  folderStatus: FolderStatusEnum;
  folder: any;
}) {
  const { setLoading } = useContext(AuthContext);
  const router = useRouter();
  const queryClient = useQueryClient();
  const [openAccessibilityModal, setOpenAccessibilityModal] =
    useState<boolean>(false);

  const toast = useToast();

  const handleLeaveGroup = async () => {
    setLoading(true);
    try {
      await leaveFolderOrGroups({ id: folderId });
      queryClient.refetchQueries([GROUP_ROLE_ACCESS, folderId]);
      queryClient.refetchQueries([USER_JOINED_GROUPS_USER_WALL]);
      queryClient.refetchQueries([FEED_GROUPS_SUGGESTION]);
      queryClient.refetchQueries([MEMBER_LIST, folderId]);
      toast('Rời nhóm thành công.', 'success');
    } catch (error) {
      toast('Có lỗi xảy ra.', 'error');
    }
    setLoading(false);
  };

  const isIJoinThisGroup = checkIsUserJoined(mineRole as ROLE_ACCESS);

  return (
    <>
      <div
        className={`my-2 flex flex-shrink-0 items-center gap-2 text-sm sm:justify-end lg:text-base`}
      >
        <div className="flex-1 md:flex-none">
          {isIJoinThisGroup ? (
            <LeaveGroupBtn handleLeaveGroup={handleLeaveGroup} />
          ) : !isIJoinThisGroup ? (
            folderStatus === FolderStatusEnum.Private ? (
              <RequestJoinGroupButton
                groupId={folderId}
                groupPrivacyStatus={folder?.status}
                size={'!h-full !min-w-[146px] !min-h-[36px]'}
                initContent={'Tham gia nhóm'}
                undoContent={'Hủy yêu cầu'}
                className="!h-full !w-full !flex-1 !rounded-md !px-[22.5px] !py-[10px] !text-sm !font-semibold !leading-[16px] !tracking-[-0.1px] !shadow-none"
                variant={'contained'}
                containerClassname="w-full md:w-auto"
              />
            ) : (
              <ButtonJoinPublicGroup
                folderId={folderId}
                folderName={folder?.folderName}
                toast={toast}
              />
            )
          ) : null}
        </div>

        <div className="w-fit">
          <ShareButton
            onClick={() =>
              mineRole == ROLE_ACCESS.Editor
                ? setOpenAccessibilityModal(true)
                : copyToClipboard(window.location.href, () =>
                    toast('Sao chép thành công!', 'success')
                  )
            }
          />
        </div>
      </div>

      {openAccessibilityModal && (
        <Accessibility
          isVisible={openAccessibilityModal}
          toggleModal={() => setOpenAccessibilityModal(false)}
          folderId={folderId}
          redirectWhenFetchMemberListFailed={(_err: any) => {
            if (_err.response.data.statusCode === 403) {
              router.replace(PATH_NAME.MY_GROUPS);
            }
          }}
        />
      )}
    </>
  );
}

function ButtonJoinPublicGroup({
  folderId,
  toast,
  folderName,
}: {
  folderId: string;
  folderName: string;
  toast: (_content: string, _type: 'success' | 'error') => void;
}) {
  const { setLoading } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const router = useRouter();

  const handleJoinGroup = async () => {
    setLoading(true);
    try {
      await postJoinGroup(folderId, FolderStatusEnum.Public);
      queryClient.refetchQueries([GROUP_ROLE_ACCESS, folderId]);
      queryClient.refetchQueries([USER_JOINED_GROUPS]);
      toast('Bạn giờ là thành viên của ' + folderName + '!', 'success');
    } catch (error) {
      toast('Có lỗi xảy ra.', 'error');
    }
    setLoading(false);
  };

  return (
    <QuickLoginModalHOC
      handleClickWhenDisabledQuickLogin={handleJoinGroup}
      loginSuccessCb={() => {
        // add param to auto join fo user
        handleJoinGroup();
        router.push({
          pathname: PATH_NAME.MY_GROUPS_DETAIL,
          query: {
            gid: folderId,
          },
        });
      }}
    >
      <button className="w-full rounded-md bg-[#3CACDD] !py-[10px] px-[22.5px] text-center text-sm font-semibold leading-[16px] tracking-[-0.1px] text-[#FCFCFC] transition-all hover:bg-[#CFF0FF] hover:text-[#242424] sm:w-fit">
        Tham gia nhóm
      </button>
    </QuickLoginModalHOC>
  );
}

export default OtherRoleButtonGroups;
